import { MonthYear } from '@common/neo.tax';
import { colors, FormStickyHeaderMemo, Icon, TwoColumnLayoutMemo, useModal } from '@ui-components/neo.tax';
import { ReactComponent as BusinessDocumentationIcon } from '@ui-components/neo.tax/dist/icons/business-documentation.svg';
import { ReactComponent as BusinessPurposeIcon } from '@ui-components/neo.tax/dist/icons/business-purpose.svg';
import { ReactComponent as CompanyIcon } from '@ui-components/neo.tax/dist/icons/company.svg';
import { ReactComponent as FinancesIcon } from '@ui-components/neo.tax/dist/icons/finances.svg';
import { ReactComponent as HistoryIcon } from '@ui-components/neo.tax/dist/icons/history.svg';
import { ReactComponent as MapIcon } from '@ui-components/neo.tax/dist/icons/map.svg';
import { ReactComponent as ProductProcessIcon } from '@ui-components/neo.tax/dist/icons/product-process.svg';
import React from 'react';

import { useQuestionActionButtonProps, useShortYearPeriodQuestion } from '@neo.tax/rd/hooks';
import { QuestionSection as QuestionSectionType, QuestionCode } from '@neo.tax/shared';

import { ActionButtonModal } from './ActionButtonModal';
import { QuestionSectionModal, SectionContent } from './company-info.styled';
import { prepareQuestions } from './company-info.utils';
import { Question } from './Question';

const sectionIconComponent = (sectionDisplayText: string): SvgrComponent => {
  switch (sectionDisplayText) {
    case 'Business Details':
      return CompanyIcon;
    case 'Company Address':
      return MapIcon;
    case 'Company History':
      return HistoryIcon;
    case 'Business Finances':
      return FinancesIcon;
    case 'Business Activity':
      return BusinessPurposeIcon;
    case 'R&D Process':
      return ProductProcessIcon;
    case 'Business Documentation':
      return BusinessDocumentationIcon;
    default:
      return CompanyIcon;
  }
};

export const QuestionSection: React.FC<{
  questionSection: QuestionSectionType;
  taxYear: number;
  disabledQuestions?: QuestionCode[];
}> = ({ disabledQuestions, questionSection: { displaySubtext, displayText, questions }, taxYear }) => {
  const SectionIcon = sectionIconComponent(displayText);

  const {
    isShortYearDirty,
    setShortYearEndMonthYear,
    setShortYearStartMonthYear,
    shortYearEndMonthYear,
    shortYearStartMonthYear,
  } = useShortYearPeriodQuestion({ questions });

  const questionSectionActionButtonProps = useQuestionActionButtonProps({
    questions,
    shortYearStartMonthYear,
    shortYearEndMonthYear,
    isShortYearDirty,
  });

  const { isOpen: isActionButtonModalOpen, setIsOpen: setIsActionButtonModalOpen } = useModal();

  const handleOpenModal = (): void => {
    setIsActionButtonModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsActionButtonModalOpen(false);
  };

  return (
    <>
      <TwoColumnLayoutMemo
        leftColumn={
          <FormStickyHeaderMemo
            title={displayText}
            subtitle={displaySubtext}
            icon={<Icon node={<SectionIcon color={colors.gray[500]} />} size={6} />}
          />
        }
      >
        <SectionContent direction="column" boxShadow="base" borderRadius="md" backgroundColor={colors.white}>
          {prepareQuestions(questions)
            .filter((question) => !disabledQuestions?.includes(question.questionCode))
            .map((question) => {
              const actionButtonProps = questionSectionActionButtonProps.get(question.questionCode);
              const actionButtonModalProps = actionButtonProps?.actionButtonModalProps;
              if (actionButtonModalProps) {
                actionButtonModalProps.openModal = handleOpenModal;
              }
              return (
                <Question
                  key={question.id}
                  question={question}
                  taxYear={taxYear}
                  actionButtonProps={actionButtonProps}
                  shortYearStartMonthYear={{
                    monthYear: shortYearStartMonthYear,
                    updateMonthYear: (monthYear: MonthYear) => setShortYearStartMonthYear(monthYear),
                  }}
                  shortYearEndMonthYear={{
                    monthYear: shortYearEndMonthYear,
                    updateMonthYear: (monthYear: MonthYear) => setShortYearEndMonthYear(monthYear),
                  }}
                  actionButtonModal={
                    actionButtonModalProps && (
                      <QuestionSectionModal isOpen={isActionButtonModalOpen} setIsOpen={handleCloseModal}>
                        <ActionButtonModal {...actionButtonModalProps} closeModal={handleCloseModal} />
                      </QuestionSectionModal>
                    )
                  }
                />
              );
            })}
        </SectionContent>
      </TwoColumnLayoutMemo>
    </>
  );
};
